import { useEffect, useRef } from "react";

/**
 * WebGL film-strip backdrop rendered behind the Instagram phone collage.
 *
 * Each thumbnail is a Three.js PlaneGeometry positioned and rotated along a
 * parametric curve: `z = -(2t - t²) * Z_DEPTH`. Steep at the bottom, flat
 * toward the top — strips appear to bend up and back like a curved tube
 * viewed from inside. 14 columns scroll along the curve in alternating
 * directions, sized large enough to fill the section edge-to-edge.
 *
 * Persistent appearance: each material is 40% opacity (no color tint) so the
 * strip is dim against the section's black background without altering the
 * thumbnail colors.
 *
 * Reveal: scroll-bound. A CSS gradient overlay starts fully black; its
 * --fs-reveal CSS variable is updated by a rAF-throttled scroll listener
 * based on how far the section has scrolled through the viewport. As the
 * user scrolls down, the overlay lifts from bottom to top, revealing the
 * strip. Scrolling back up re-covers it. `prefers-reduced-motion` shows the
 * strip fully revealed and skips scroll tracking.
 *
 * Three.js is dynamically imported. Animation pauses via IntersectionObserver
 * when offscreen.
 */

interface Props {
  cols?: number;
  thumbsPerCol?: number;
  thumbCount?: number;
}

const FILM_STRIP_CSS = `
  @property --fs-reveal {
    syntax: '<percentage>';
    inherits: false;
    initial-value: -10%;
  }

  /* Persistent gradient — always present, sits between canvas and reveal overlay.
     Top of section stays fully black; fades to transparent by 50% height; below
     50% is fully clear so the strip is unobstructed. */
  .fs-static-gradient {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(to top, transparent 50%, rgba(0,0,0,0.2) 100%);
  }

  /* Scroll-bound reveal — sits on top of everything; lifts away as user scrolls in. */
  .fs-reveal-overlay {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(
      to top,
      transparent var(--fs-reveal),
      #000 calc(var(--fs-reveal) + 10%)
    );
  }

  @media (prefers-reduced-motion: reduce) {
    .fs-reveal-overlay { --fs-reveal: 100%; }
  }
`;

const FilmStripBackdrop = ({
  cols = 8,
  thumbsPerCol = 6,
  thumbCount = 100,
}: Props) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    const canvas = canvasRef.current;
    if (!root || !canvas) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    let cancelled = false;
    let started = false;
    let teardown = () => { /* assigned later inside the async block */ };

    const start = async () => {
      if (started || cancelled) return;
      started = true;

      let THREE: typeof import("three");
      try {
        THREE = await import("three");
      } catch {
        // three.js chunk failed to load — the backdrop is decorative, stay blank.
        return;
      }
      if (cancelled) return;

      let W = root.clientWidth;
      let H = root.clientHeight;

      const scene = new THREE.Scene();

      const camera = new THREE.PerspectiveCamera(70, W / H, 0.1, 100);
      camera.position.set(0, 0.4, 2.4);
      camera.lookAt(0, 1.0, 0);

      let renderer: import("three").WebGLRenderer;
      try {
        renderer = new THREE.WebGLRenderer({
          canvas,
          antialias: true,
          alpha: true,
        });
      } catch {
        // WebGL unavailable or blocked (hardened/older browsers). The backdrop is
        // purely decorative (aria-hidden), so fail silently instead of throwing
        // an uncaught error.
        return;
      }
      renderer.setSize(W, H, false);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

      // Curve constants — extended Y range so the strip's top vanishes off-screen
      // (above the visible viewport top), not at the visible top edge.
      const Y_MIN = -6;
      const Y_MAX = 10;
      const Z_DEPTH = 9;

      const curve = (t: number) => {
        const y = Y_MIN + t * (Y_MAX - Y_MIN);
        const z = -(2 * t - t * t) * Z_DEPTH;
        const dy = Y_MAX - Y_MIN;
        const dz = -(2 - 2 * t) * Z_DEPTH;
        const rotX = Math.atan2(dz, dy);
        return { y, z, rotX };
      };

      // Spacing larger than thumb width creates a visible gap between columns.
      const COL_SPACING = 2.4;
      const THUMB_W = 2.0;
      const THUMB_H = THUMB_W * 1.25;
      const CORNER_RADIUS = 0.18;

      // Dim only — no color tint. Low opacity blends with black bg for atmospheric feel.
      const TINT_OPACITY = 0.22;

      // Shared shader for all thumb materials. Uses a rounded-rectangle signed
      // distance function to mask out the corners of each plane.
      const VERTEX_SHADER = `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `;
      const FRAGMENT_SHADER = `
        varying vec2 vUv;
        uniform sampler2D map;
        uniform float opacity;
        uniform vec2 planeSize;
        uniform float radius;
        void main() {
          vec2 p = (vUv - 0.5) * planeSize;
          vec2 halfSize = planeSize * 0.5;
          vec2 d = abs(p) - halfSize + radius;
          float sdf = min(max(d.x, d.y), 0.0) + length(max(d, 0.0)) - radius;
          float alpha = 1.0 - smoothstep(-0.01, 0.01, sdf);
          if (alpha < 0.01) discard;
          vec4 texColor = texture2D(map, vUv);
          gl_FragColor = vec4(texColor.rgb, texColor.a * opacity * alpha);
        }
      `;

      const loader = new THREE.TextureLoader();
      type AnimatedQuad = {
        mesh: THREE.Mesh;
        t: number;
        dir: number;
        speed: number;
      };
      const animated: AnimatedQuad[] = [];
      const textures: THREE.Texture[] = [];
      const materials: THREE.ShaderMaterial[] = [];
      const geometries: THREE.PlaneGeometry[] = [];

      let loadedCount = 0;
      let initialRendered = false;
      const totalToLoad = cols * thumbsPerCol;

      const renderInitialFrame = () => {
        if (initialRendered) return;
        animated.forEach((a) => {
          const { y, z, rotX } = curve(a.t);
          a.mesh.position.y = y;
          a.mesh.position.z = z;
          a.mesh.rotation.x = rotX;
        });
        renderer.render(scene, camera);
        initialRendered = true;
      };

      for (let c = 0; c < cols; c++) {
        const colX = (c - cols / 2 + 0.5) * COL_SPACING;
        const dir = c % 2 === 0 ? 1 : -1;
        // ~half the previous speed (was 0.025 + (c % 4) * 0.006)
        const speed = 0.012 + (c % 4) * 0.003;

        for (let i = 0; i < thumbsPerCol; i++) {
          const thumbIdx = ((c * thumbsPerCol + i) % thumbCount) + 1;
          const filename = `${String(thumbIdx).padStart(3, "0")}.webp`;

          const tex = loader.load(`/film-thumbs/${filename}`, () => {
            loadedCount++;
            if (loadedCount >= totalToLoad * 0.3) renderInitialFrame();
          });
          tex.colorSpace = THREE.SRGBColorSpace;
          textures.push(tex);

          const geom = new THREE.PlaneGeometry(THUMB_W, THUMB_H);
          geometries.push(geom);

          const mat = new THREE.ShaderMaterial({
            uniforms: {
              map: { value: tex },
              opacity: { value: TINT_OPACITY },
              planeSize: {
                value: new THREE.Vector2(THUMB_W, THUMB_H),
              },
              radius: { value: CORNER_RADIUS },
            },
            vertexShader: VERTEX_SHADER,
            fragmentShader: FRAGMENT_SHADER,
            transparent: true,
            side: THREE.FrontSide,
          });
          materials.push(mat);

          const mesh = new THREE.Mesh(geom, mat);
          mesh.position.x = colX;
          scene.add(mesh);

          animated.push({
            mesh,
            t: i / thumbsPerCol,
            dir,
            speed,
          });
        }
      }

      // IntersectionObserver: pause rAF when offscreen.
      let isVisible = false;
      const obs = new IntersectionObserver(
        ([entry]) => {
          isVisible = entry.isIntersecting;
        },
        { threshold: 0 },
      );
      obs.observe(root);

      // Scroll-bound reveal: --fs-reveal interpolates from -10% to 100% as the
      // section scrolls through the viewport. 0% progress = section just entering
      // from below; 100% progress = section's bottom has crossed the viewport's
      // bottom edge (whole section has been scrolled through).
      const overlay = overlayRef.current;
      const updateReveal = () => {
        if (!overlay || !root) return;
        const rect = root.getBoundingClientRect();
        const vh = window.innerHeight;
        // Reveal progresses from when the section starts entering until the
        // section's TOP reaches roughly 1/3 of the way up the viewport.
        // That way the reveal completes while the section is comfortably in view,
        // not just at the moment it begins to appear.
        const startY = vh;            // section top here -> progress 0
        const endY = vh * 0.33;       // section top here -> progress 1
        const progress = Math.max(
          0,
          Math.min(1, (startY - rect.top) / (startY - endY)),
        );
        const revealPct = -10 + progress * 110;
        overlay.style.setProperty("--fs-reveal", `${revealPct}%`);
      };

      let scrollRaf = 0;
      const onScroll = () => {
        if (scrollRaf) return;
        scrollRaf = requestAnimationFrame(() => {
          scrollRaf = 0;
          updateReveal();
        });
      };

      if (!prefersReducedMotion) {
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll, { passive: true });
        updateReveal();
      }

      // Keep renderer in sync with container size
      const ro = new ResizeObserver((entries) => {
        const { width, height } = entries[0].contentRect;
        if (width === W && height === H) return;
        W = width;
        H = height;
        renderer.setSize(W, H, false);
        camera.aspect = W / H;
        camera.updateProjectionMatrix();
        if (initialRendered) renderer.render(scene, camera);
      });
      ro.observe(root);

      // Animation loop
      let lastTime = 0;
      let rafId = 0;

      const tick = (now: number) => {
        if (cancelled) return;
        const dt = lastTime ? Math.min((now - lastTime) / 1000, 0.05) : 0.016;
        lastTime = now;

        if (!prefersReducedMotion && isVisible && initialRendered) {
          animated.forEach((a) => {
            a.t = (a.t + a.dir * a.speed * dt + 2) % 1;
            const { y, z, rotX } = curve(a.t);
            a.mesh.position.y = y;
            a.mesh.position.z = z;
            a.mesh.rotation.x = rotX;
          });
          renderer.render(scene, camera);
        }

        rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);

      teardown = () => {
        cancelAnimationFrame(rafId);
        if (scrollRaf) cancelAnimationFrame(scrollRaf);
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onScroll);
        obs.disconnect();
        ro.disconnect();
        textures.forEach((t) => t.dispose());
        materials.forEach((m) => m.dispose());
        geometries.forEach((g) => g.dispose());
        // Release the WebGL context, not just three's resources — otherwise it
        // leaks across SPA navigations until the GPU process hits its limit.
        renderer.forceContextLoss();
        renderer.dispose();
      };
    };

    // Defer the heavy three.js import + WebGL init + texture loads until the
    // section approaches the viewport, so the decorative backdrop never competes
    // with the hero LCP on initial load. rootMargin starts it just before it
    // scrolls into view.
    const startObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          startObserver.disconnect();
          start();
        }
      },
      { rootMargin: "300px" },
    );
    startObserver.observe(root);

    return () => {
      cancelled = true;
      startObserver.disconnect();
      teardown();
    };
  }, [cols, thumbsPerCol, thumbCount]);

  return (
    <div
      ref={rootRef}
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none overflow-hidden"
    >
      <style>{FILM_STRIP_CSS}</style>
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
      />
      <div className="fs-static-gradient" />
      <div ref={overlayRef} className="fs-reveal-overlay" />
    </div>
  );
};

export default FilmStripBackdrop;
